import gsap from '../../js/gsap/gsap.min.js';
import { ScrollTrigger } from '../../js/gsap/ScrollTrigger.min.js';
import { Flip } from '../../js/gsap/Flip.min.js';
import { lenis } from '../../js/theme/_lenis.js';

gsap.registerPlugin(ScrollTrigger, Flip);

console.log(`
██╗      ██████╗  █████╗ ██████╗ ███████╗██████╗
██║     ██╔═══██╗██╔══██╗██╔══██╗██╔════╝██╔══██╗
██║     ██║   ██║███████║██║  ██║█████╗  ██║  ██║
██║     ██║   ██║██╔══██║██║  ██║██╔══╝  ██║  ██║
███████╗╚██████╔╝██║  ██║██████╔╝███████╗██████╔╝
╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚══════╝╚═════╝
`);


// =============================================================================
// Resources grid filter
// =============================================================================
function initResourceFilter() {
	var grid = document.querySelector( '[data-resources-grid]' );
	if ( ! grid ) {
		return;
	}

	var buttons = Array.prototype.slice.call( document.querySelectorAll( '[data-filter]' ) );
	var cards   = Array.prototype.slice.call( grid.querySelectorAll( '[data-category]' ) );

	function setState( filter ) {
		cards.forEach( function ( card ) {
			var matches = 'all' === filter || card.dataset.category === filter;
			card.classList.toggle( 'is-hidden', ! matches );
		} );
	}

	function applyFilter( filter ) {
		if ( document.startViewTransition ) {
			document.startViewTransition( function () { setState( filter ); } );
		} else {
			setState( filter );
		}
	}

	buttons.forEach( function ( btn ) {
		btn.addEventListener( 'click', function () {
			buttons.forEach( function ( b ) { b.classList.remove( 'is-active' ); } );
			btn.classList.add( 'is-active' );
			applyFilter( btn.dataset.filter );
		} );
	} );
}


// =============================================================================
// Mobile menu
// =============================================================================
function initMenu() {
	var toggle = document.querySelector( '[data-menu-toggle]' );
	var menu   = document.getElementById( 'site-menu' );
	if ( ! toggle || ! menu ) {
		return;
	}

	var closeBtn = menu.querySelector( '[data-menu-close]' );

	function openMenu() {
		menu.classList.add( 'is-open' );
		menu.setAttribute( 'aria-hidden', 'false' );
		document.body.classList.add( 'menu-open' );
		toggle.setAttribute( 'aria-expanded', 'true' );
		closeBtn.focus();
	}

	function closeMenu() {
		menu.classList.remove( 'is-open' );
		menu.setAttribute( 'aria-hidden', 'true' );
		document.body.classList.remove( 'menu-open' );
		toggle.setAttribute( 'aria-expanded', 'false' );
		toggle.focus();
	}

	toggle.addEventListener( 'click', openMenu );
	closeBtn.addEventListener( 'click', closeMenu );

	document.addEventListener( 'keydown', function ( e ) {
		if ( 'Escape' === e.key && menu.classList.contains( 'is-open' ) ) {
			closeMenu();
		}
	} );
}


// =============================================================================
// Learning nav
// =============================================================================
function initLearningNav() {
	var nav = document.querySelector( '.learning-nav' );
	if ( ! nav ) {
		return;
	}

	var toggle = nav.querySelector( '[data-learning-toggle]' );
	if ( ! toggle ) {
		return;
	}

	toggle.addEventListener( 'click', function () {
		var open = nav.classList.toggle( 'is-open' );
		toggle.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
	} );

	document.addEventListener( 'click', function ( e ) {
		if ( ! nav.contains( e.target ) && nav.classList.contains( 'is-open' ) ) {
			nav.classList.remove( 'is-open' );
			toggle.setAttribute( 'aria-expanded', 'false' );
		}
	} );

	document.addEventListener( 'keydown', function ( e ) {
		if ( 'Escape' === e.key && nav.classList.contains( 'is-open' ) ) {
			nav.classList.remove( 'is-open' );
			toggle.setAttribute( 'aria-expanded', 'false' );
			toggle.focus();
		}
	} );
}


// =============================================================================
// Olivetto hero — scrubbed accent motion
// =============================================================================
function initOlivettoHero() {
	const container = document.querySelector( '.olivetto-container' );
	if ( ! container ) return;

	const card = container.querySelector( '.olivetto-hero__card' );
	const navy = card.querySelector( '.olivetto-hero__accent--navy' );
	const teal = card.querySelector( '.olivetto-hero__accent--teal' );

	const mm = gsap.matchMedia();

	mm.add( '(min-width: 768px)', () => {
		gsap.timeline( {
			scrollTrigger: {
				trigger: container,
				start: 'top 60%',
				end: 'bottom 60%',
				scrub: true,
				invalidateOnRefresh: true,
			},
		} )
			// Navy rides down the right edge for the whole scroll
			.to( navy, {
				y: () => card.offsetHeight - navy.offsetHeight,
				duration: 0.5,
				ease: 'none',
			}, 0 )
			// Teal slides along the bottom, right → left, from halfway
			.to( teal, {
				x: () => -( card.offsetWidth - teal.offsetWidth ),
				duration: 0.5,
				ease: 'none',
			}, 0.5 );
	} );
}


// =============================================================================
// Footer leaves — sequential colour flip on scroll into view
// =============================================================================
function initFooterLeaves() {
	const svg = document.querySelector( '#footer-leaves' );
	if ( ! svg ) return;                       // <-- the guard that was missing

	const PALETTE = [ '#B3C625', '#6FB9AD', '#DB7A68', '#C5443D', '#F5E6CD' ];
	const footer  = svg.closest( '.leaf-container' );
	const leaves  = gsap.utils.toArray( '#footer-leaves path' );

	gsap.set( leaves, { transformOrigin: '50% 50%' } );

	// build the sequential flip once, paused
	const flip = gsap.timeline( { paused: true } );
	leaves.forEach( ( leaf, i ) => {
		const at    = i * 0.12;                    // stagger — each leaf 0.12s after the last
		const color = PALETTE[ i % PALETTE.length ]; // next colour in the set, cycling
		flip
			.to(  leaf, { scaleX: 0, duration: 0.3, ease: 'power2.in'  }, at )
			.set( leaf, { fill: color },                                  at + 0.3 )
			.to(  leaf, { scaleX: 1, duration: 0.3, ease: 'power2.out' }, at + 0.3 );
	} );

	// play forward on enter, reverse on leave
	new IntersectionObserver( ( [ entry ] ) => {
		entry.isIntersecting ? flip.play() : flip.reverse();
	}, { threshold: 0.25 } ).observe( footer );
}


// =============================================================================
// Parallax images
// =============================================================================
function initParallax() {
	document.querySelectorAll( '.image--parallax' ).forEach( ( wrap ) => {
		gsap.fromTo(
			wrap.querySelector( '.image__img' ),
			{ yPercent: -8 },
			{
				yPercent: 8,
				ease: 'none',
				scrollTrigger: {
					trigger: wrap,
					start: 'top bottom',
					end: 'bottom top',
					scrub: true,
				},
			}
		);
	} );
}


// =============================================================================
// Map — staggered pop-in + Olivetto glow (plays once)
// =============================================================================
function initMap() {
	const contentMap = document.querySelector( '.map' );
	if ( ! contentMap ) return;

	const mapsvg = document.querySelector( '.map svg' );
	const groups = mapsvg.querySelectorAll(
		':scope > g[id]' +
		':not(#PinJar_Road)' +
		':not(#Wanneroo_Road)' +
		':not(#Mitchell_Freeway)'+
    ':not(#Joondalup_Drive)'
	);
	const olivetto = mapsvg.querySelector( '#Olivetto' );

	// Starting pose for the pop-in. Scale about each group's own centre.
	gsap.set( groups, { scale: 0.8, transformOrigin: 'center center' } );

	const io = new IntersectionObserver( ( entries, obs ) => {
		if ( ! entries[ 0 ].isIntersecting ) return;

		gsap.to( groups, {
			opacity: 1,
			scale: 1,
			duration: 0.5,
			ease: 'back.out(1.7)',
			stagger: { each: 0.05, from: 'random' },
		} );

		olivetto.classList.add( 'is-glowing' );
		obs.disconnect(); // play once
	}, { threshold: 0.25 } );

	io.observe( mapsvg );
}


// =============================================================================
// Content-section banner — morph / flip loop + edge-matched banner colour
// =============================================================================
function initContentBanner() {
	const contentBanner = document.querySelector( '#content-section-banner' );
	if ( ! contentBanner ) return;

	gsap.set( '#drop-group', { y: -95, scale: 0.45, opacity: 0, transformOrigin: '50% 50%' } );

	// ---- MORPH: image 1 -> image 2, rippling clockwise TL -> TR -> BR -> BL ----
	const morph = gsap.timeline( { paused: true, defaults: { ease: 'power3.inOut' } } );
	morph
		.to( '#leaf-flip',   { scaleX: -1, duration: 1.6, transformOrigin: '50% 50%' }, 0 )
		.to( '#petal-left',  { fill: '#b5c627', duration: 0.5, ease: 'power2.inOut' }, 0.55 )
		.to( '#petal-right', { fill: '#c4453d', duration: 0.5, ease: 'power2.inOut' }, 0.55 )
		.to( '#tr-circle',   { attr: { r: 240 }, duration: 1.5, ease: 'expo.inOut' }, 0.2 )
		.to( '#br-big',      { attr: { r: 0 }, duration: 1.5 }, 0.45 )
		.fromTo( '#drop-group', { y: -95, scale: 0.45 }, { y: 0, scale: 1, duration: 0.85, ease: 'power2.in' }, 0.7 )
		.fromTo( '#drop-group', { opacity: 0 }, { opacity: 1, duration: 0.35, ease: 'power1.out' }, 0.7 )
		.to( '#drop-group', { scaleY: 0.84, scaleX: 1.1, duration: 0.12, ease: 'power1.out' }, 1.55 )
		.to( '#drop-group', { scaleY: 1, scaleX: 1, duration: 0.35, ease: 'back.out(2)' }, 1.67 );

	// ---- TURN: Flip every tile one slot clockwise ----
	const cells = gsap.utils.toArray( '.cell' );
	const slots = [ [ 0, 0 ], [ 240, 0 ], [ 240, 240 ], [ 0, 240 ] ];
	cells.forEach( ( c, i ) => { c._slot = i; gsap.set( c, { x: slots[ i ][ 0 ], y: slots[ i ][ 1 ] } ); } );

	function turn() {
		const state = Flip.getState( cells );
		cells.forEach( c => {
			c._slot = ( c._slot + 1 ) % 4;
			const [ x, y ] = slots[ c._slot ];
			gsap.set( c, { x, y } );
		} );
		return Flip.from( state, { duration: 1.5, ease: 'power2.inOut' } )
			.to( cells, { scale: 1, duration: 0.75, ease: 'power2.in',  transformOrigin: '50% 50%' }, 0 )
			.to( cells, { scale: 1, duration: 0.75, ease: 'power2.out' }, 0.75 );
	}

	// ---- BANNER: match whatever meets the animation's left edge ----
	const banner = document.querySelector( '.left-banner' );
	const NAVY = '#0d1b3e';
	const fillOf = sel => getComputedStyle( document.querySelector( sel ) ).fill;
	const nearest = ( x, y ) => cells.reduce( ( a, b ) =>
		Math.hypot( gsap.getProperty( b, 'x' ) - x, gsap.getProperty( b, 'y' ) - y ) <
		Math.hypot( gsap.getProperty( a, 'x' ) - x, gsap.getProperty( a, 'y' ) - y ) ? b : a );

	function edgeColor( cell ) {
		if ( cell.id === 'cell-leaves' ) {
			const flipped = gsap.getProperty( '#leaf-flip', 'scaleX' ) < 0;
			return fillOf( flipped ? '#petal-right' : '#petal-left' );
		}
		return cell.dataset.edge || NAVY;
	}

	let currentBannerColor = null;

	gsap.ticker.add( () => {
		const top = edgeColor( nearest( 0, 0 ) );
		const color = top !== NAVY ? top : edgeColor( nearest( 0, 240 ) );

		if ( color !== currentBannerColor ) {
			currentBannerColor = color;

			gsap.to( banner, {
				backgroundColor: color,
				duration: 0.5,
				ease: 'power2.out',
				overwrite: true,
			} );
		}
	} );

	// ---- SEQUENCE ----
	const wait = s => gsap.delayedCall( s, () => {} );
	async function run() {
		for ( ;; ) {
			await morph.play( 0 );
			await wait( 0.6 );
			await turn();
			await wait( 0.4 );
			await morph.reverse();
			await wait( 0.6 );
			await turn();
			await wait( 0.4 );
		}
	}

	run();
}


// =============================================================================
// Entry point — DOM is parsed before a deferred ES module runs, but this stays
// correct even if the bundle is ever loaded as a classic script.
// =============================================================================
function init() {
	initResourceFilter();
	initMenu();
	initLearningNav();
	initOlivettoHero();
	initFooterLeaves();
	initParallax();
	initMap();
	initContentBanner();
}

if ( document.readyState !== 'loading' ) {
	init();
} else {
	document.addEventListener( 'DOMContentLoaded', init );
}
